import { formatDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Users,
  Edit,
  Trash2,
  MoreVertical,
  Search,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL } from "../../api/header";
import { all_routes } from "../../Router/all_routes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";
const CustomerGroups = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [searchText, setSearchText] = useState("");
  const [modalData, setModalData] = useState({
    id: null,
    name: "",
    description: "",
    is_default: null,
    status: null,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Queries
  const { data: customergroups = [] } = useQuery({
    queryKey: ["customer-groups"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/customer_group`);
      return res.data.data || [];
    },
  });

  // Mutations
  const saveCustomerGroupMutation = useMutation({
    mutationFn: async (data) => {
      const url = data.id
        ? `${API_BASE_URL}/customer_group/${data.id}`
        : `${API_BASE_URL}/customer_group`;
      const method = data.id ? "put" : "post";
      return await apiClient[method](url, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-groups"] });
      toast.success(
        `Customer Group ${modalData.id ? "updated" : "added"} successfully`,
      );
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong!");
    },
  });

  const deleteCustomerGroupMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(`${API_BASE_URL}/customer_group/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-groups"] });
      toast.success("Customer Group deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong!");
    },
  });

  useEffect(() => {
    if (!hasPermission("customer group read")) {
      toast.error("Unauthorized", {
        description:
          "You do not have permission to view this page. Redirecting to dashboard...",
      });
      setTimeout(() => {
        navigate(all_routes.dashboard);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!modalData.name) {
      toast.error("Name is required");
      return;
    }
    saveCustomerGroupMutation.mutate(modalData);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteCustomerGroupMutation.mutate(deleteId);
    setIsDeleteDialogOpen(false);
  };

  const openModal = (role = null) => {
    if (role) {
      setModalData(role);
      setIsEdit(true);
    } else {
      setModalData({
        id: null,
        name: "",
        description: "",
        status: null,
        is_default: null,
      });
      setIsEdit(false);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => row.original.description || "-",
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => formatDate(row.original.created_at),
    },
    {
      accessorKey: "is_default",
      header: "Is Default",
      cell: ({ row }) => (
        <Badge variant="outline">
          {row.original.is_default ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.status ? "success" : "destructive"}>
          {row.original.status ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("customer group update") && (
              <DropdownMenuItem onClick={() => openModal(row.original)}>
                <Edit className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
            )}
            {hasPermission("customer group delete") && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => handleDelete(row.original.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const filteredData = customergroups.filter((entry) => {
    return Object.keys(entry).some((key) => {
      return String(entry[key])
        .toLowerCase()
        .includes(searchText.toLowerCase());
    });
  });

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Customer Groups"
          subtitle="Manage Your Customer Groups"
        />
        <PageHeaderActions>
          {hasPermission("customer group create") && (
            <Button onClick={() => openModal()}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add Customer Group
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search groups..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          enableRowSelection={true}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Groups: {filteredData.length || 0}
            </span>
          }
        />
      </TableCard>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit" : "Add"} Customer Group</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="Enter group name"
                value={modalData.name}
                onChange={(e) =>
                  setModalData({ ...modalData, name: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter description"
                value={modalData.description}
                onChange={(e) =>
                  setModalData({
                    ...modalData,
                    description: e.target.value,
                  })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Is Default?</Label>
                <ReactSelect
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                  value={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ].find((opt) => opt.value === modalData.is_default)}
                  onChange={(opt) =>
                    setModalData({
                      ...modalData,
                      is_default: opt ? opt.value : false,
                    })
                  }
                  placeholder="Select"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <ReactSelect
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "Inactive" },
                  ]}
                  value={[
                    { value: true, label: "Active" },
                    { value: false, label: "Inactive" },
                  ].find((opt) => opt.value === modalData.status)}
                  onChange={(opt) =>
                    setModalData({
                      ...modalData,
                      status: opt ? opt.value : false,
                    })
                  }
                  placeholder="Select"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="justify-start border-t pt-4">
            <Button
              onClick={handleSave}
              disabled={saveCustomerGroupMutation.isPending}
            >
              {saveCustomerGroupMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              customer group and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default CustomerGroups;

